// =========================================================
// Vojjin Cookies Utilities
// =========================================================
import {registerGlobal} from "./core.js";

class Cookies {

    /**
     * Retrieves the value of a specified cookie by its name.
     *
     * @param {string} name - The name of the cookie to retrieve.
     * @returns {string|null} The value of the cookie if found, or null if the cookie does not exist.
     */
    static get = (name) => {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    }

    /**
     * Sets a cookie with the specified name, value, and options.
     *
     * @param {string} name - The name of the cookie.
     * @param {string} value - The value to be stored in the cookie.
     * @param {Object} [opts={}] - Optional settings for the cookie (e.g., path, domain, maxAge, secure, etc.).
     *   If not provided, the cookie defaults to a path of '/'.
     */
    static set = (name, value, opts = {}) => {
        const options = {
            path: '/',
            ...opts
        };
        let cookie = `${name}=${encodeURIComponent(value)}`;

        for (const [key, val] of Object.entries(options)) {
            cookie += '; ' + key;
            if (val !== true) cookie += '=' + val;
        }

        document.cookie = cookie;
    }

    /**
     * Deletes a browser cookie by setting its expiration date to a past date.
     *
     * @param {string} name - The name of the cookie to be deleted.
     * @param {string} [path='/'] - The path where the cookie was set. Defaults to '/'.
     */
    static del = (name, path = '/') => {
        document.cookie = `${name}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }

    /**
     * Checks whether a specified cookie exists.
     *
     * @param {string} name - The name of the cookie to check.
     * @returns {boolean} Returns `true` if the cookie with the specified name exists, otherwise `false`.
     */
    static has = (name) => {
        return this.get(name) !== null;
    }

    /**
     * Retrieves all cookies as an object.
     *
     * The `all` function parses the document's cookies and converts them
     * into a key-value object. Each cookie's key is used as the object key
     * and its value is URI-decoded and set as the corresponding object value.
     *
     * @returns {Object} An object containing all cookies, where the keys are
     * the cookie names and the values are the respective decoded cookie values.
     */
    static all = () => {
        return document.cookie.split('; ').reduce((acc, pair) => {
            const [k, v] = pair.split('=');
            if (k) acc[k] = decodeURIComponent(v);
            return acc;
        }, {});
    }

    /**
     * Clears all cookies stored by the application.
     *
     * This function deletes all cookies currently available by iterating
     * through the list of cookie names and removing each one.
     *
     * @param {string} [path='/'] - The path where the cookies should be cleared.
     *                              Defaults to the root path ('/').
     */
    static clear = (path = '/') => {
        for (const name of Object.keys(this.all())) {
            this.del(name, path);
        }
    }
}

registerGlobal("Cookies", Cookies);
